import { useState } from 'react';
import { useRefundPayment } from './hooks/useDisputes';
import Button from '../../shared/components/Button';

interface Props {
  /** Optional pre-filled payment reference. */
  reference?: string;
  onClose: () => void;
}

function nairaToKobo(naira: string): number {
  return Math.round(parseFloat(naira) * 100);
}

export function RefundModal({ reference: initialRef = '', onClose }: Props) {
  const refund = useRefundPayment();
  const [reference, setReference] = useState(initialRef);
  const [amountNaira, setAmountNaira] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!reference.trim()) { setError('Payment reference is required'); return; }

    let amount: number | undefined;
    if (amountNaira.trim()) {
      amount = nairaToKobo(amountNaira);
      if (isNaN(amount) || amount <= 0) { setError('Refund amount must be greater than zero'); return; }
    }

    await refund.mutateAsync({ reference: reference.trim(), amount });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 overflow-y-auto py-8">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-md mx-4 p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">Refund a Payment</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-muted-foreground text-2xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Payment Reference *</label>
            <input
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              placeholder="e.g. pay_ref_abc123"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Amount (₦)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              placeholder="Leave blank for a full refund"
              value={amountNaira}
              onChange={(e) => setAmountNaira(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">Leave blank to refund the full amount.</p>
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
              Cancel
            </button>
            <Button type="submit" disabled={refund.isPending}>
              {refund.isPending ? 'Refunding…' : 'Refund'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
